export type PdfFindMatchPart = {
   itemIndex: number;
   start: number;
   end: number;
};

export type PdfFindMatch = {
   page: number;
   itemIndex: number;
   start: number;
   end: number;
   parts: PdfFindMatchPart[];
};

export function findMatchKey(match: PdfFindMatch): string {
   const last = match.parts[match.parts.length - 1];
   return last
      ? `${match.page}:${match.itemIndex}:${match.start}:${last.itemIndex}:${last.end}`
      : `${match.page}:${match.itemIndex}:${match.start}:${match.end}`;
}

export function isSameFindMatch(a?: PdfFindMatch | null, b?: PdfFindMatch | null): boolean {
   if (!a || !b) {
      return false;
   }
   return a.page === b.page
      && a.itemIndex === b.itemIndex
      && a.start === b.start
      && a.parts.length === b.parts.length
      && a.parts.every((part, index) => {
         const other = b.parts[index];
         return Boolean(
            other
            && part.itemIndex === other.itemIndex
            && part.start === other.start
            && part.end === other.end
         );
      });
}

export const MAX_PDF_FIND_MATCHES = 1000;
export const PDF_SEARCH_PAGES_PER_SLICE = 6;
export const PDF_VIEWER_MOUNTED_PAGE_WINDOW = 2;
/** Must match the visual gap between stacked PDF pages. */
export const PDF_VIEWER_PAGE_GAP = 40;
export const PDF_VIEWER_STACK_END_PAD = 48;

type TextItemLike = { str?: string };

function asTextItems(items: ReadonlyArray<unknown>): TextItemLike[] {
   return items.map((item) => {
      if (item && typeof item === "object" && "str" in item && typeof (item as TextItemLike).str === "string") {
         return { str: (item as TextItemLike).str };
      }
      return { str: "" };
   });
}

function escapeHtml(value: string): string {
   return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
}

export function normalizeFindQuery(raw: string): string {
   return raw.trim().toLowerCase();
}

export function findMatchesInPageItems(
   page: number,
   items: ReadonlyArray<unknown>,
   query: string,
): PdfFindMatch[] {
   const q = normalizeFindQuery(query);
   if (!q || page < 1) {
      return [];
   }
   const textItems = asTextItems(items);
   const map: Array<{ itemIndex: number; offset: number }> = [];
   let haystack = "";
   textItems.forEach((item, itemIndex) => {
      const str = typeof item.str === "string" ? item.str : "";
      for (let i = 0; i < str.length; i++) {
         map.push({ itemIndex, offset: i });
         haystack += str[i];
      }
   });
   const lower = haystack.toLowerCase();
   const found: PdfFindMatch[] = [];
   let pos = 0;
   while (pos <= lower.length - q.length) {
      const idx = lower.indexOf(q, pos);
      if (idx === -1) {
         break;
      }
      const startMap = map[idx];
      const endMap = map[idx + q.length - 1];
      if (startMap && endMap) {
         const parts: PdfFindMatchPart[] = [];
         for (let itemIndex = startMap.itemIndex; itemIndex <= endMap.itemIndex; itemIndex++) {
            const item = textItems[itemIndex];
            const str = typeof item?.str === "string" ? item.str : "";
            parts.push({
               itemIndex,
               start: itemIndex === startMap.itemIndex ? startMap.offset : 0,
               end: itemIndex === endMap.itemIndex ? endMap.offset + 1 : str.length,
            });
         }
         const first = parts[0];
         if (first) {
            found.push({
               page,
               itemIndex: first.itemIndex,
               start: first.start,
               end: first.end,
               parts,
            });
         }
      }
      pos = idx + 1;
   }
   return found;
}

export function highlightTextItemHtml(
   str: string,
   ranges: Array<{ start: number; end: number; current?: boolean; key?: string }>,
): string {
   if (!str) {
      return "";
   }
   const usable = ranges
      .map((range) => ({
         start: Math.max(0, Math.min(str.length, range.start)),
         end: Math.max(0, Math.min(str.length, range.end)),
         current: Boolean(range.current),
         key: range.key,
      }))
      .filter((range) => range.end > range.start)
      .sort((a, b) => a.start - b.start || b.end - a.end);
   if (!usable.length) {
      return escapeHtml(str);
   }
   let html = "";
   let cursor = 0;
   usable.forEach((range) => {
      if (range.start < cursor) {
         return;
      }
      if (range.start > cursor) {
         html += escapeHtml(str.slice(cursor, range.start));
      }
      const markClass = range.current ? "ann-pdf-findHit ann-pdf-findHit--current" : "ann-pdf-findHit";
      const keyAttr = range.key ? ` data-ann-find="${escapeHtml(range.key)}"` : "";
      html += `<mark class="${markClass}"${keyAttr}>${escapeHtml(str.slice(range.start, range.end))}</mark>`;
      cursor = range.end;
   });
   if (cursor < str.length) {
      html += escapeHtml(str.slice(cursor));
   }
   return html;
}

export function rangesForTextItem(
   matches: PdfFindMatch[],
   page: number,
   itemIndex: number,
   currentMatch?: PdfFindMatch | null,
): Array<{ start: number; end: number; current?: boolean; key: string }> {
   return matches.flatMap((match) => {
      if (match.page !== page) {
         return [];
      }
      const key = findMatchKey(match);
      return match.parts
         .filter((part) => part.itemIndex === itemIndex)
         .map((part) => ({
            start: part.start,
            end: part.end,
            current: isSameFindMatch(currentMatch, match),
            key,
         }));
   });
}

export function mountedPagesAround(center: number, numPages: number, windowSize = PDF_VIEWER_MOUNTED_PAGE_WINDOW): number[] {
   const lo = Math.max(1, center - windowSize);
   const hi = Math.min(numPages, center + windowSize);
   const pages: number[] = [];
   for (let page = lo; page <= hi; page++) {
      pages.push(page);
   }
   return pages;
}

export function pdfPageSlotHeight(pageHeight: number, gap = PDF_VIEWER_PAGE_GAP): number {
   return Math.max(1, pageHeight) + gap;
}

export function pageFromScrollOffset(scrollTop: number, slotHeight: number, numPages: number): number {
   if (numPages < 1 || slotHeight <= 0) {
      return 1;
   }
   return Math.max(1, Math.min(numPages, Math.floor(Math.max(0, scrollTop) / slotHeight) + 1));
}

export function scrollOffsetForPage(page: number, slotHeight: number): number {
   return Math.max(0, (Math.max(1, page) - 1) * Math.max(0, slotHeight));
}

export function virtualPdfStackHeight(
   numPages: number,
   slotHeight: number,
   endPad = PDF_VIEWER_STACK_END_PAD,
): number {
   return Math.max(0, numPages) * Math.max(0, slotHeight) + Math.max(0, endPad);
}

export function mountedWindowSizeForViewport(
   viewportHeight: number,
   slotHeight: number,
   extra = 2,
): number {
   const visible = Math.max(1, Math.ceil(Math.max(0, viewportHeight) / Math.max(1, slotHeight)));
   return Math.max(PDF_VIEWER_MOUNTED_PAGE_WINDOW, Math.ceil(visible / 2) + extra);
}

export function samePageSet(a: ReadonlySet<number>, b: ReadonlySet<number>): boolean {
   if (a.size !== b.size) {
      return false;
   }
   for (const page of b) {
      if (!a.has(page)) {
         return false;
      }
   }
   return true;
}

export function fitPageWidthScale(containerWidth: number, pageWidth: number, padding = 32): number {
   if (containerWidth <= 0 || pageWidth <= 0) {
      return 1;
   }
   return Math.max(0.35, Math.min(3, (containerWidth - padding) / pageWidth));
}
